import type { FC, ReactNode } from 'react'
import type { Transition } from 'framer-motion'
import type { HeroListing, ListingFlag } from '@/components/home/market-hero/hero-market-data'

import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'

import { cn } from '@/lib/utils'
import { TONE_PLATE } from '@/components/home/market-hero/hero-market-data'

/**
 * One floating marketplace listing.
 *
 * EVERYTHING IS SIZED IN `em`. The card never sets a pixel dimension of its
 * own, so the composition scales it by setting a single `font-size` on the
 * piece that holds it — one number per breakpoint instead of a responsive
 * variant on every rule inside. The two exceptions are deliberate: the 2px
 * border and the offset shadow stay fixed, because a hairline that scales
 * reads as a mistake rather than as depth.
 *
 * The SHELL is static and only the CONTENT swaps, so a rotation moves the
 * text without the border and shadow flickering underneath it. `min-h` on the
 * shell is what holds the card's height steady while `AnimatePresence` has
 * one listing on its way out and the next not yet in — without it the card
 * would collapse for 200ms on every rotation.
 *
 * The NEW/HOT tag sits on the CORNER rather than in the top row, which is
 * both what the reference does and what leaves the row wide enough for a full
 * handle: in the row it cost about two ems and truncated `@warrior.albania`.
 */

const FLAG_LABEL: Record<ListingFlag, string> = {
    new: 'New',
    hot: 'Hot'
}

interface FloatingListingCardProps {
    listing: HeroListing
    /**
     * The rotation the piece rests at, in degrees. Hover animates against it
     * so the card straightens slightly as it lifts, which is what makes it
     * read as picked up rather than merely scaled.
     */
    restRotation?: number
    className?: string
}

export const FloatingListingCard: FC<FloatingListingCardProps> = ({
    listing,
    restRotation = 0,
    className
}): ReactNode => {
    const reduced = useReducedMotion()
    const Icon = listing.icon
    /* The brand easing curve, so a card swap moves like everything else. */
    const swap: Transition = { duration: reduced ? 0 : 0.22, ease: [0.22, 1, 0.36, 1] }

    return (
        <motion.div
            initial={false}
            animate={{ scale: 1, y: 0, rotate: 0 }}
            whileHover={reduced ? undefined : { scale: 1.035, y: -5, rotate: restRotation * -0.5 }}
            transition={{ type: 'spring', stiffness: 320, damping: 24, mass: 0.7 }}
            className={cn(
                'relative flex min-h-[7.1em] flex-col rounded-[0.85em] border-2 border-ink-900 bg-paper p-[0.8em]',
                'shadow-offset transition-shadow duration-200 ease-mta hover:shadow-[7px_7px_0_var(--border-ink)]',
                className
            )}
        >
            <AnimatePresence initial={false}>
                {listing.flag ? (
                    <motion.span
                        key={listing.flag}
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.7 }}
                        transition={swap}
                        className='hero-label absolute -right-[0.35em] -top-[0.45em] rounded-[0.35em] border-2 border-ink-900 bg-brand-red px-[0.5em] py-[0.1em] text-[0.56em] leading-[1.6] tracking-[0.06em] text-white'
                    >
                        {FLAG_LABEL[listing.flag]}
                    </motion.span>
                ) : null}
            </AnimatePresence>

            <AnimatePresence mode='wait' initial={false}>
                <motion.div
                    key={listing.id}
                    initial={{ opacity: 0, y: 7 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -6 }}
                    transition={swap}
                    className='flex flex-1 flex-col gap-[0.7em]'
                >
                    <div className='flex items-start gap-[0.6em]'>
                        <span
                            className='grid size-[2.35em] shrink-0 place-items-center rounded-[0.5em] border-2 border-ink-900'
                            style={{ backgroundColor: TONE_PLATE[listing.tone] }}
                        >
                            <Icon className='size-[1.2em] text-ink-900' strokeWidth={2.4} aria-hidden='true' />
                        </span>

                        <div className='min-w-0 flex-1'>
                            <p className='type-eyebrow truncate text-[0.62em] leading-[1.35] text-text-muted'>
                                {listing.label}
                            </p>

                            <p className='truncate text-[0.9em] font-bold leading-[1.35] text-ink-900'>
                                {listing.detail}
                            </p>
                        </div>
                    </div>

                    <div className='mt-auto flex items-end justify-between gap-[0.5em]'>
                        <p className='text-[1.4em] font-extrabold leading-none tracking-[-0.025em] text-ink-900'>
                            {listing.price}
                        </p>

                        <p className='min-w-0 truncate text-[0.62em] font-medium leading-[1.4] text-text-muted'>
                            {listing.handle}
                        </p>
                    </div>
                </motion.div>
            </AnimatePresence>
        </motion.div>
    )
}
